import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon, InfoIcon, LockIcon, CreditCardIcon, TagIcon, UserIcon, CalendarIcon, MailIcon, PhoneIcon, MapPinIcon, StarIcon } from 'lucide-react';
import Button from '../components/common/Button';
import FlightCard from '../components/search/FlightCard';
import HotelCard from '../components/search/HotelCard';
import HotelDetailsDrawer from '../components/search/HotelDetailsDrawer';
import PackageCard from '../components/search/PackageCard';
import { Flight, Hotel, Package } from '../utils/types';
import { flights, hotels, packages } from '../utils/mockData';
import RecommendedPackages from '../components/booking/RecommendedPackages';
const BookingFlow = () => {
  const {
    step
  } = useParams<{
    step: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(step || 'select');
  // State for selected items
  const [selectedFlight, setSelectedFlight] = useState<Flight | null>(null);
  const [selectedHotel, setSelectedHotel] = useState<Hotel | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  // State for hotel details drawer
  const [isHotelDetailsOpen, setIsHotelDetailsOpen] = useState(false);
  const [selectedHotelForDetails, setSelectedHotelForDetails] = useState<Hotel | null>(null);
  const [selectedRoomType, setSelectedRoomType] = useState<string>('');
  const [selectedRoomPrice, setSelectedRoomPrice] = useState<number>(0);
  // Form state for booking details
  const [formData, setFormData] = useState({
    // Contact details
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    // Payment details
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
    cardName: '',
    // Special requests
    specialRequests: '',
    // Passenger details
    passengers: [{
      title: 'Mr',
      firstName: '',
      lastName: '',
      dateOfBirth: ''
    }]
  });
  // Determine if we're booking a package
  const bookingType = searchParams.get('type') || 'flight';
  const isPackageBooking = bookingType === 'package';
  // Load selected items from URL params if available
  useEffect(() => {
    const flightId = searchParams.get('flightId');
    const hotelId = searchParams.get('hotelId');
    const packageId = searchParams.get('packageId');
    if (flightId) {
      const flight = flights.find(f => f.id === flightId);
      if (flight) setSelectedFlight(flight);
    }
    if (hotelId) {
      const hotel = hotels.find(h => h.id === hotelId);
      if (hotel) setSelectedHotel(hotel);
    }
    if (packageId) {
      const pkg = packages.find(p => p.id === packageId);
      if (pkg) {
        setSelectedPackage(pkg);
        setSelectedFlight(pkg.flight);
        setSelectedHotel(pkg.hotel);
      }
    }
    // For package bookings, ensure flight is selected first
    if (isPackageBooking && !selectedFlight && currentStep !== 'select') {
      navigate('/booking/select');
      setCurrentStep('select');
    }
  }, [searchParams, isPackageBooking]);
  // Update current step based on URL param
  useEffect(() => {
    if (step) {
      setCurrentStep(step);
    }
  }, [step]);
  // Handle opening hotel details drawer
  const handleViewHotelDetails = (hotel: Hotel) => {
    setSelectedHotelForDetails(hotel);
    setIsHotelDetailsOpen(true);
  };
  // Handle selecting a room from the hotel details drawer
  const handleSelectRoom = (roomType: string, price: number) => {
    setSelectedRoomType(roomType);
    setSelectedRoomPrice(price);
    setSelectedHotel(selectedHotelForDetails);
    setIsHotelDetailsOpen(false);
    if (isPackageBooking) {
      navigate('/booking/prebook');
      setCurrentStep('prebook');
    }
  };
  const goToNextStep = () => {
    switch (currentStep) {
      case 'select':
        if (isPackageBooking) {
          // For package booking, if flight is selected, go to hotel selection
          if (selectedFlight) {
            navigate('/booking/select-hotel');
            setCurrentStep('select-hotel');
          } else {
            // If no flight selected, stay on this page
            alert('Please select a flight to continue');
          }
        } else {
          // For non-package bookings, go directly to pre-book
          navigate('/booking/prebook');
          setCurrentStep('prebook');
        }
        break;
      case 'select-hotel':
        if (selectedHotel) {
          navigate('/booking/prebook');
          setCurrentStep('prebook');
        } else {
          alert('Please select a hotel to continue');
        }
        break;
      case 'prebook':
        navigate('/booking/review');
        setCurrentStep('review');
        break;
      case 'review':
        navigate('/booking/payment');
        setCurrentStep('payment');
        break;
      case 'payment':
        navigate('/confirmation');
        break;
      default:
        navigate('/booking/select');
        setCurrentStep('select');
    }
  };
  const handleSelectFlight = (flight: Flight) => {
    setSelectedFlight(flight);
    if (isPackageBooking) {
      navigate('/booking/select-hotel');
      setCurrentStep('select-hotel');
    } else {
      navigate('/booking/prebook');
      setCurrentStep('prebook');
    }
  };
  const handleSelectHotel = (hotel: Hotel) => {
    setSelectedHotel(hotel);
    navigate('/booking/prebook');
    setCurrentStep('prebook');
  };
  const handleSelectPackage = (pkg: Package) => {
    setSelectedPackage(pkg);
    setSelectedFlight(pkg.flight);
    setSelectedHotel(pkg.hotel);
    navigate('/booking/prebook');
    setCurrentStep('prebook');
  };
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    // Handle nested passenger data
    if (name.startsWith('passenger-')) {
      const [prefix, index, field] = name.split('-');
      const passengerIndex = parseInt(index);
      const updatedPassengers = [...formData.passengers];
      // Ensure passenger at this index exists
      while (updatedPassengers.length <= passengerIndex) {
        updatedPassengers.push({
          title: 'Mr',
          firstName: '',
          lastName: '',
          dateOfBirth: ''
        });
      }
      updatedPassengers[passengerIndex] = {
        ...updatedPassengers[passengerIndex],
        [field]: value
      };
      setFormData({
        ...formData,
        passengers: updatedPassengers
      });
    } else {
      // Handle regular form fields
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  const handleAddPassenger = () => {
    setFormData({
      ...formData,
      passengers: [...formData.passengers, {
        title: 'Mr',
        firstName: '',
        lastName: '',
        dateOfBirth: ''
      }]
    });
  };
  const handleRemovePassenger = (index: number) => {
    if (formData.passengers.length > 1) {
      const updatedPassengers = [...formData.passengers];
      updatedPassengers.splice(index, 1);
      setFormData({
        ...formData,
        passengers: updatedPassengers
      });
    }
  };
  const handleSubmitPayment = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/confirmation');
  };
  const calculateTotalPrice = () => {
    if (selectedPackage) {
      return selectedPackage.totalPrice;
    }
    let total = 0;
    if (selectedFlight) {
      total += selectedFlight.price;
    }
    if (selectedHotel) {
      // Use selected room price if available, otherwise use default hotel price
      const hotelPrice = selectedRoomPrice > 0 ? selectedRoomPrice : selectedHotel.pricePerNight;
      total += hotelPrice * 7; // Assuming 7 nights
    }
    return total;
  };
  return <div className="bg-gray-50 min-h-screen w-full" data-id="element-628">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-629">
        {/* Progress bar */}
        <div className="mb-8" data-id="element-630">
          <div className="flex items-center justify-between w-full" data-id="element-631">
            <div className={`flex flex-col items-center ${currentStep === 'select' || currentStep === 'select-hotel' ? 'text-blue-600' : 'text-gray-500'}`} data-id="element-632">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'select' || currentStep === 'select-hotel' ? 'bg-blue-100 border-2 border-blue-600' : 'bg-gray-100'}`} data-id="element-633">
                1
              </div>
              <span className="mt-2 text-xs sm:text-sm" data-id="element-634">
                {isPackageBooking ? 'Select Flight & Hotel' : 'Select'}
              </span>
            </div>
            <div className={`flex-grow h-0.5 ${currentStep === 'select' || currentStep === 'select-hotel' ? 'bg-gray-200' : 'bg-blue-600'}`} data-id="element-635" />
            <div className={`flex flex-col items-center ${currentStep === 'prebook' ? 'text-blue-600' : currentStep === 'select' || currentStep === 'select-hotel' ? 'text-gray-500' : 'text-blue-600'}`} data-id="element-636">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'prebook' ? 'bg-blue-100 border-2 border-blue-600' : currentStep === 'select' || currentStep === 'select-hotel' ? 'bg-gray-100' : 'bg-blue-100 border-2 border-blue-600'}`} data-id="element-637">
                2
              </div>
              <span className="mt-2 text-xs sm:text-sm" data-id="element-638">Pre-book</span>
            </div>
            <div className={`flex-grow h-0.5 ${currentStep === 'select' || currentStep === 'select-hotel' || currentStep === 'prebook' ? 'bg-gray-200' : 'bg-blue-600'}`} data-id="element-639" />
            <div className={`flex flex-col items-center ${currentStep === 'review' ? 'text-blue-600' : currentStep === 'payment' ? 'text-blue-600' : 'text-gray-500'}`} data-id="element-640">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'review' ? 'bg-blue-100 border-2 border-blue-600' : currentStep === 'payment' ? 'bg-blue-100 border-2 border-blue-600' : 'bg-gray-100'}`} data-id="element-641">
                3
              </div>
              <span className="mt-2 text-xs sm:text-sm" data-id="element-642">Review</span>
            </div>
            <div className={`flex-grow h-0.5 ${currentStep === 'payment' ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-643" />
            <div className={`flex flex-col items-center ${currentStep === 'payment' ? 'text-blue-600' : 'text-gray-500'}`} data-id="element-644">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'payment' ? 'bg-blue-100 border-2 border-blue-600' : 'bg-gray-100'}`} data-id="element-645">
                4
              </div>
              <span className="mt-2 text-xs sm:text-sm" data-id="element-646">Payment</span>
            </div>
          </div>
        </div>
        {/* Back button for navigation */}
        {currentStep !== 'select' && <div className="mb-4" data-id="element-647">
            <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-blue-600" data-id="element-648">
              <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-649" />
              <span data-id="element-650">Back</span>
            </button>
          </div>}
        {/* Step content */}
        <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-651">
          {currentStep === 'select' && <div data-id="element-652">
              <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-653">
                {isPackageBooking ? 'Select Your Flight' : 'Select Your Flight'}
              </h1>
              <div className="space-y-6" data-id="element-654">
                {flights.map(flight => <div key={flight.id} onClick={() => handleSelectFlight(flight)} className={`cursor-pointer ${selectedFlight?.id === flight.id ? 'ring-2 ring-blue-500' : ''}`} data-id="element-655">
                    <FlightCard flight={flight} data-id="element-656" />
                  </div>)}
              </div>
              {isPackageBooking && selectedFlight && <div className="mt-6 flex justify-end" data-id="element-657">
                  <Button variant="primary" onClick={goToNextStep} data-id="element-658">
                    Continue to Select Hotel
                  </Button>
                </div>}
            </div>}
          {currentStep === 'select-hotel' && <div data-id="element-659">
              <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-660">
                Select Your Hotel
              </h1>
              {isPackageBooking && selectedFlight && <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-md" data-id="element-661">
                  <h3 className="font-medium text-blue-800 mb-2" data-id="element-662">
                    Selected Flight:
                  </h3>
                  <p className="text-sm text-blue-700" data-id="element-663">
                    {selectedFlight.airline} - {selectedFlight.flightNumber}{' '}
                    from {selectedFlight.departureCity} to{' '}
                    {selectedFlight.arrivalCity}
                  </p>
                  <p className="text-sm text-blue-700" data-id="element-664">
                    {new Date(selectedFlight.departureTime).toLocaleDateString()}{' '}
                    at{' '}
                    {new Date(selectedFlight.departureTime).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
                  </p>
                </div>}
              <div className="space-y-6" data-id="element-665">
                {hotels.map(hotel => <div key={hotel.id} className={`${selectedHotel?.id === hotel.id ? 'ring-2 ring-blue-500' : ''}`} data-id="element-666">
                    <HotelCard hotel={hotel} onViewDetails={handleViewHotelDetails} data-id="element-667" />
                  </div>)}
              </div>
              {/* Recommended packages section */}
              <RecommendedPackages selectedFlight={selectedFlight} onSelectPackage={handleSelectPackage} data-id="element-668" />
              {selectedHotel && <div className="mt-6 flex justify-end" data-id="element-669">
                  <Button variant="primary" onClick={goToNextStep} data-id="element-670">
                    Continue to Pre-book
                  </Button>
                </div>}
            </div>}
          {currentStep === 'prebook' && <div data-id="element-671">
              <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-672">
                Pre-book Confirmation
              </h1>
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6 flex items-start" data-id="element-673">
                <InfoIcon className="h-5 w-5 text-blue-500 mr-2 mt-0.5" data-id="element-674" />
                <div data-id="element-675">
                  <h3 className="text-blue-700 font-medium" data-id="element-676">Almost there!</h3>
                  <p className="text-blue-600 text-sm" data-id="element-677">
                    Review your selected items below before proceeding to the
                    final booking details.
                  </p>
                </div>
              </div>
              {/* Show selected items */}
              {selectedFlight && <div className="mb-6" data-id="element-678">
                  <h2 className="text-lg font-medium text-gray-900 mb-3" data-id="element-679">
                    Selected Flight
                  </h2>
                  <FlightCard flight={selectedFlight} data-id="element-680" />
                </div>}
              {selectedHotel && <div className="mb-6" data-id="element-681">
                  <h2 className="text-lg font-medium text-gray-900 mb-3" data-id="element-682">
                    Selected Hotel
                  </h2>
                  <HotelCard hotel={selectedHotel} onViewDetails={handleViewHotelDetails} data-id="element-683" />
                  {selectedRoomType && <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-md" data-id="element-684">
                      <h3 className="font-medium text-blue-800" data-id="element-685">
                        Selected Room Type:
                      </h3>
                      <p className="text-sm text-blue-700" data-id="element-686">
                        {selectedRoomType} - ${selectedRoomPrice} per night
                      </p>
                    </div>}
                </div>}
              {selectedPackage && <div className="mb-6" data-id="element-687">
                  <h2 className="text-lg font-medium text-gray-900 mb-3" data-id="element-688">
                    Selected Package
                  </h2>
                  <PackageCard package={selectedPackage} data-id="element-689" />
                </div>}
              <div className="mt-8 flex justify-end" data-id="element-690">
                <Button variant="primary" onClick={goToNextStep} data-id="element-691">
                  Continue to Review
                </Button>
              </div>
            </div>}
          {currentStep === 'review' && <div data-id="element-692">
              <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-693">
                Review Your Booking
              </h1>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-694">
                <div data-id="element-695">
                  <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-696">
                    Traveler Information
                  </h2>
                  <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-697">
                    <form className="space-y-4" data-id="element-698">
                      {formData.passengers.map((passenger, index) => <div key={index} className="border-b border-gray-200 pb-4 mb-4 last:border-b-0 last:pb-0 last:mb-0" data-id="element-699">
                          <div className="flex justify-between items-center mb-2" data-id="element-700">
                            <h3 className="font-medium" data-id="element-701">
                              {index === 0 ? 'Primary Traveler' : `Traveler ${index + 1}`}
                            </h3>
                            {index > 0 && <button type="button" onClick={() => handleRemovePassenger(index)} className="text-sm text-red-600 hover:text-red-800" data-id="element-702">
                                Remove
                              </button>}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-703">
                            <div data-id="element-704">
                              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-705">
                                Title
                              </label>
                              <select name={`passenger-${index}-title`} value={passenger.title} onChange={handleFormChange} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" data-id="element-706">
                                <option value="Mr" data-id="element-707">Mr</option>
                                <option value="Mrs" data-id="element-708">Mrs</option>
                                <option value="Ms" data-id="element-709">Ms</option>
                                <option value="Dr" data-id="element-710">Dr</option>
                              </select>
                            </div>
                            <div data-id="element-711">
                              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-712">
                                First Name
                              </label>
                              <input type="text" name={`passenger-${index}-firstName`} value={passenger.firstName} onChange={handleFormChange} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" required data-id="element-713" />
                            </div>
                            <div data-id="element-714">
                              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-715">
                                Last Name
                              </label>
                              <input type="text" name={`passenger-${index}-lastName`} value={passenger.lastName} onChange={handleFormChange} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" required data-id="element-716" />
                            </div>
                          </div>
                          <div className="mt-3" data-id="element-717">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-718">
                              Date of Birth
                            </label>
                            <input type="date" name={`passenger-${index}-dateOfBirth`} value={passenger.dateOfBirth} onChange={handleFormChange} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" required data-id="element-719" />
                          </div>
                        </div>)}
                      <button type="button" onClick={handleAddPassenger} className="text-sm text-blue-600 font-medium hover:text-blue-800 flex items-center" data-id="element-720">
                        + Add Another Traveler
                      </button>
                    </form>
                  </div>
                  <h2 className="text-lg font-medium text-gray-900 mt-6 mb-4" data-id="element-721">
                    Contact Information
                  </h2>
                  <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-722">
                    <form className="space-y-4" data-id="element-723">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-724">
                        <div data-id="element-725">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-726">
                            First Name
                          </label>
                          <input type="text" name="firstName" value={formData.firstName} onChange={handleFormChange} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" required data-id="element-727" />
                        </div>
                        <div data-id="element-728">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-729">
                            Last Name
                          </label>
                          <input type="text" name="lastName" value={formData.lastName} onChange={handleFormChange} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" required data-id="element-730" />
                        </div>
                      </div>
                      <div data-id="element-731">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-732">
                          Email
                        </label>
                        <div className="relative" data-id="element-733">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-734">
                            <MailIcon className="h-4 w-4 text-gray-400" data-id="element-735" />
                          </div>
                          <input type="email" name="email" value={formData.email} onChange={handleFormChange} className="block w-full pl-10 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="email@example.com" required data-id="element-736" />
                        </div>
                      </div>
                      <div data-id="element-737">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-738">
                          Phone
                        </label>
                        <div className="relative" data-id="element-739">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-740">
                            <PhoneIcon className="h-4 w-4 text-gray-400" data-id="element-741" />
                          </div>
                          <input type="tel" name="phone" value={formData.phone} onChange={handleFormChange} className="block w-full pl-10 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="+1 (123) 456-7890" required data-id="element-742" />
                        </div>
                      </div>
                    </form>
                  </div>
                  <h2 className="text-lg font-medium text-gray-900 mt-6 mb-4" data-id="element-743">
                    Special Requests
                  </h2>
                  <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-744">
                    <textarea name="specialRequests" value={formData.specialRequests} onChange={handleFormChange} rows={4} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Any special requests or requirements..." data-id="element-745"></textarea>
                  </div>
                </div>
                <div data-id="element-746">
                  <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-747">
                    Booking Summary
                  </h2>
                  <div className="bg-white border border-gray-200 rounded-lg p-4 sticky top-6" data-id="element-748">
                    {/* Flight details */}
                    {selectedFlight && <div className="mb-4 pb-4 border-b border-gray-200" data-id="element-749">
                        <h3 className="font-medium text-gray-900 mb-2" data-id="element-750">
                          Flight
                        </h3>
                        <div className="flex justify-between items-start" data-id="element-751">
                          <div data-id="element-752">
                            <p className="text-sm font-medium" data-id="element-753">
                              {selectedFlight.airline} -{' '}
                              {selectedFlight.flightNumber}
                            </p>
                            <p className="text-sm text-gray-600" data-id="element-754">
                              {selectedFlight.departureCity} (
                              {selectedFlight.departureAirport}) to{' '}
                              {selectedFlight.arrivalCity} (
                              {selectedFlight.arrivalAirport})
                            </p>
                            <p className="text-sm text-gray-600" data-id="element-755">
                              {new Date(selectedFlight.departureTime).toLocaleDateString()}{' '}
                              at{' '}
                              {new Date(selectedFlight.departureTime).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                            </p>
                          </div>
                          <span className="text-blue-600 font-medium" data-id="element-756">
                            ${selectedFlight.price}
                          </span>
                        </div>
                      </div>}
                    {/* Hotel details */}
                    {selectedHotel && <div className="mb-4 pb-4 border-b border-gray-200" data-id="element-757">
                        <h3 className="font-medium text-gray-900 mb-2" data-id="element-758">
                          Hotel
                        </h3>
                        <div className="flex justify-between items-start" data-id="element-759">
                          <div data-id="element-760">
                            <p className="text-sm font-medium" data-id="element-761">
                              {selectedHotel.name}
                            </p>
                            <div className="flex items-center" data-id="element-762">
                              {Array(selectedHotel.stars).fill(0).map((_, i) => <StarIcon key={i} className="h-3 w-3 text-yellow-400" fill="currentColor" data-id="element-763" />)}
                            </div>
                            <p className="text-sm text-gray-600 mt-1" data-id="element-764">
                              <span className="flex items-center" data-id="element-765">
                                <MapPinIcon className="h-3 w-3 mr-1" data-id="element-766" />
                                {selectedHotel.city}, {selectedHotel.country}
                              </span>
                            </p>
                            {selectedRoomType && <p className="text-sm text-gray-600" data-id="element-767">
                                {selectedRoomType}
                              </p>}
                            <p className="text-sm text-gray-600" data-id="element-768">7 nights</p>
                          </div>
                          <span className="text-blue-600 font-medium" data-id="element-769">
                            $
                            {(selectedRoomPrice > 0 ? selectedRoomPrice : selectedHotel.pricePerNight) * 7}
                          </span>
                        </div>
                      </div>}
                    {/* Package savings */}
                    {selectedPackage && <div className="mb-4 pb-4 border-b border-gray-200" data-id="element-770">
                        <div className="flex justify-between items-center" data-id="element-771">
                          <div className="flex items-center text-green-600" data-id="element-772">
                            <TagIcon className="h-4 w-4 mr-1" data-id="element-773" />
                            <span className="font-medium" data-id="element-774">Package Savings</span>
                          </div>
                          <span className="text-green-600 font-medium" data-id="element-775">
                            -${selectedPackage.savings}
                          </span>
                        </div>
                      </div>}
                    {/* Taxes and fees */}
                    <div className="mb-4 pb-4 border-b border-gray-200" data-id="element-776">
                      <div className="flex justify-between items-center" data-id="element-777">
                        <span className="text-gray-600" data-id="element-778">Taxes & Fees</span>
                        <span className="text-gray-600" data-id="element-779">$120.00</span>
                      </div>
                    </div>
                    {/* Total */}
                    <div className="flex justify-between items-center font-bold text-lg" data-id="element-780">
                      <span data-id="element-781">Total</span>
                      <span data-id="element-782">${calculateTotalPrice() + 120}</span>
                    </div>
                    <div className="mt-6" data-id="element-783">
                      <Button variant="primary" onClick={goToNextStep} fullWidth data-id="element-784">
                        Proceed to Payment
                      </Button>
                    </div>
                    <div className="mt-4 text-sm text-gray-500 flex items-center justify-center" data-id="element-785">
                      <LockIcon className="h-3 w-3 mr-1" data-id="element-786" />
                      <span data-id="element-787">Secure payment process</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
          {currentStep === 'payment' && <div data-id="element-788">
              <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-789">Payment</h1>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-790">
                <div data-id="element-791">
                  <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-792">
                    Payment Method
                  </h2>
                  <form onSubmit={handleSubmitPayment} data-id="element-793">
                    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6" data-id="element-794">
                      <div className="space-y-4" data-id="element-795">
                        <div className="flex items-center" data-id="element-796">
                          <input id="creditCard" name="paymentMethod" type="radio" className="h-4 w-4 text-blue-600 border-gray-300" defaultChecked data-id="element-797" />
                          <label htmlFor="creditCard" className="ml-3 block" data-id="element-798">
                            <div className="flex items-center" data-id="element-799">
                              <CreditCardIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-800" />
                              <span className="text-gray-700" data-id="element-801">
                                Credit or Debit Card
                              </span>
                            </div>
                          </label>
                        </div>
                        <div className="flex items-center" data-id="element-802">
                          <input id="paypal" name="paymentMethod" type="radio" className="h-4 w-4 text-blue-600 border-gray-300" data-id="element-803" />
                          <label htmlFor="paypal" className="ml-3 block" data-id="element-804">
                            <div className="flex items-center" data-id="element-805">
                              <span className="text-blue-600 font-bold mr-2" data-id="element-806">
                                Pay
                              </span>
                              <span className="text-blue-800 font-bold" data-id="element-807">
                                Pal
                              </span>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                    <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-808">
                      Card Details
                    </h2>
                    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6" data-id="element-809">
                      <div className="space-y-4" data-id="element-810">
                        <div data-id="element-811">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-812">
                            Card Number
                          </label>
                          <div className="relative" data-id="element-813">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-814">
                              <CreditCardIcon className="h-4 w-4 text-gray-400" data-id="element-815" />
                            </div>
                            <input type="text" name="cardNumber" value={formData.cardNumber} onChange={handleFormChange} placeholder="1234 5678 9012 3456" className="block w-full pl-10 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" required data-id="element-816" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4" data-id="element-817">
                          <div data-id="element-818">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-819">
                              Expiry Date
                            </label>
                            <input type="text" name="cardExpiry" value={formData.cardExpiry} onChange={handleFormChange} placeholder="MM/YY" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" required data-id="element-820" />
                          </div>
                          <div data-id="element-821">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-822">
                              CVV
                            </label>
                            <input type="text" name="cardCvc" value={formData.cardCvc} onChange={handleFormChange} placeholder="123" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" required data-id="element-823" />
                          </div>
                        </div>
                        <div data-id="element-824">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-825">
                            Name on Card
                          </label>
                          <input type="text" name="cardName" value={formData.cardName} onChange={handleFormChange} placeholder="John Smith" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" required data-id="element-826" />
                        </div>
                      </div>
                    </div>
                    <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-827">
                      Billing Address
                    </h2>
                    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6" data-id="element-828">
                      <div className="flex items-center mb-4" data-id="element-829">
                        <input id="sameAsContact" name="billingAddress" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded" defaultChecked data-id="element-830" />
                        <label htmlFor="sameAsContact" className="ml-2 block text-sm text-gray-700" data-id="element-831">
                          Same as contact information
                        </label>
                      </div>
                    </div>
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start" data-id="element-832">
                      <InfoIcon className="h-5 w-5 text-blue-500 mr-2 mt-0.5" data-id="element-833" />
                      <p className="text-blue-700 text-sm" data-id="element-834">
                        By clicking "Complete Booking", you agree to our{' '}
                        <Link to="/" className="underline font-medium" data-id="element-835">
                          Terms and Conditions
                        </Link>{' '}
                        and{' '}
                        <Link to="/" className="underline font-medium" data-id="element-836">
                          Privacy Policy
                        </Link>
                        .
                      </p>
                    </div>
                  </form>
                </div>
                <div data-id="element-837">
                  <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-838">
                    Booking Summary
                  </h2>
                  <div className="bg-white border border-gray-200 rounded-lg p-4 sticky top-6" data-id="element-839">
                    {/* Flight details */}
                    {selectedFlight && <div className="mb-4 pb-4 border-b border-gray-200" data-id="element-840">
                        <h3 className="font-medium text-gray-900 mb-2" data-id="element-841">
                          Flight
                        </h3>
                        <div className="flex justify-between items-start" data-id="element-842">
                          <div data-id="element-843">
                            <p className="text-sm font-medium" data-id="element-844">
                              {selectedFlight.airline} -{' '}
                              {selectedFlight.flightNumber}
                            </p>
                            <p className="text-sm text-gray-600" data-id="element-845">
                              {selectedFlight.departureCity} (
                              {selectedFlight.departureAirport}) to{' '}
                              {selectedFlight.arrivalCity} (
                              {selectedFlight.arrivalAirport})
                            </p>
                            <p className="text-sm text-gray-600" data-id="element-846">
                              {new Date(selectedFlight.departureTime).toLocaleDateString()}{' '}
                              at{' '}
                              {new Date(selectedFlight.departureTime).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                            </p>
                          </div>
                          <span className="text-blue-600 font-medium" data-id="element-847">
                            ${selectedFlight.price}
                          </span>
                        </div>
                      </div>}
                    {/* Hotel details */}
                    {selectedHotel && <div className="mb-4 pb-4 border-b border-gray-200" data-id="element-848">
                        <h3 className="font-medium text-gray-900 mb-2" data-id="element-849">
                          Hotel
                        </h3>
                        <div className="flex justify-between items-start" data-id="element-850">
                          <div data-id="element-851">
                            <p className="text-sm font-medium" data-id="element-852">
                              {selectedHotel.name}
                            </p>
                            <div className="flex items-center" data-id="element-853">
                              {Array(selectedHotel.stars).fill(0).map((_, i) => <StarIcon key={i} className="h-3 w-3 text-yellow-400" fill="currentColor" data-id="element-854" />)}
                            </div>
                            <p className="text-sm text-gray-600 mt-1" data-id="element-855">
                              <span className="flex items-center" data-id="element-856">
                                <MapPinIcon className="h-3 w-3 mr-1" data-id="element-857" />
                                {selectedHotel.city}, {selectedHotel.country}
                              </span>
                            </p>
                            {selectedRoomType && <p className="text-sm text-gray-600" data-id="element-858">
                                {selectedRoomType}
                              </p>}
                            <p className="text-sm text-gray-600" data-id="element-859">7 nights</p>
                          </div>
                          <span className="text-blue-600 font-medium" data-id="element-860">
                            $
                            {(selectedRoomPrice > 0 ? selectedRoomPrice : selectedHotel.pricePerNight) * 7}
                          </span>
                        </div>
                      </div>}
                    {/* Package savings */}
                    {selectedPackage && <div className="mb-4 pb-4 border-b border-gray-200" data-id="element-861">
                        <div className="flex justify-between items-center" data-id="element-862">
                          <div className="flex items-center text-green-600" data-id="element-863">
                            <TagIcon className="h-4 w-4 mr-1" data-id="element-864" />
                            <span className="font-medium" data-id="element-865">Package Savings</span>
                          </div>
                          <span className="text-green-600 font-medium" data-id="element-866">
                            -${selectedPackage.savings}
                          </span>
                        </div>
                      </div>}
                    {/* Taxes and fees */}
                    <div className="mb-4 pb-4 border-b border-gray-200" data-id="element-867">
                      <div className="flex justify-between items-center" data-id="element-868">
                        <span className="text-gray-600" data-id="element-869">Taxes & Fees</span>
                        <span className="text-gray-600" data-id="element-870">$120.00</span>
                      </div>
                    </div>
                    {/* Total */}
                    <div className="flex justify-between items-center font-bold text-lg" data-id="element-871">
                      <span data-id="element-872">Total</span>
                      <span data-id="element-873">${calculateTotalPrice() + 120}</span>
                    </div>
                    <div className="mt-6" data-id="element-874">
                      <Button variant="primary" type="submit" onClick={handleSubmitPayment} fullWidth data-id="element-875">
                        Complete Booking
                      </Button>
                    </div>
                    <div className="mt-4 text-sm text-gray-500 flex items-center justify-center" data-id="element-876">
                      <LockIcon className="h-3 w-3 mr-1" data-id="element-877" />
                      <span data-id="element-878">Secure payment process</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </div>
      {/* Hotel Details Drawer */}
      <HotelDetailsDrawer hotel={selectedHotelForDetails} isOpen={isHotelDetailsOpen} onClose={() => setIsHotelDetailsOpen(false)} onSelectRoom={handleSelectRoom} data-id="element-879" />
    </div>;
};
export default BookingFlow;